import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import dayjs from "dayjs";
import { motion } from "framer-motion";

const MacroRing = () => {
  const { user } = useAuth();

  const [macros, setMacros] = useState([
    { name: "Protein", value: 0, target: 120, color: "#3b82f6", percentage: 0 },
    { name: "Carbs", value: 0, target: 200, color: "#f59e0b", percentage: 0 },
    { name: "Fats", value: 0, target: 60, color: "#8b5cf6", percentage: 0 },
    
  ]);

  const [totalCalories, setTotalCalories] = useState(0);

  const ringSize = 200;
  const center = ringSize / 2;

  useEffect(() => {
    if (user) fetchTodayMacros();
  }, [user]);

  const fetchTodayMacros = async () => {
    const start = dayjs().startOf("day").toISOString();

    const { data, error } = await supabase
      .from("meal_logs")
      .select("protein, carbs, fat, calories")
      .eq("user_id", user.id)
      .gte("created_at", start);

    if (error) {
      console.error("Macro fetch error:", error);
      return;
    }

    let totalProtein = 0;
    let totalCarbs = 0;
    let totalFat = 0;
    let totalKcal = 0;

    data.forEach((item) => {
      totalProtein += Number(item.protein || 0);
      totalCarbs += Number(item.carbs || 0);
      totalFat += Number(item.fat || 0);
      totalKcal += Number(item.calories || 0);
    });

    const updated = [
      {
        name: "Protein",
        value: Math.round(totalProtein),
        target: 120,
        color: "#3b82f6",
        percentage: Math.min((totalProtein / 120) * 100, 100),
      },
      {
        name: "Carbs",
        value: Math.round(totalCarbs),
        target: 200,
        color: "#f59e0b",
        percentage: Math.min((totalCarbs / 200) * 100, 100),
      },
      {
        name: "Fats",
        value: Math.round(totalFat),
        target: 60,
        color: "#8b5cf6",
        percentage: Math.min((totalFat / 60) * 100, 100),
      },
      
    ];

    setMacros(updated);
    setTotalCalories(Math.round(totalKcal));
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">
        Today&apos;s Macros
      </h3>

      <div className="flex flex-col items-center space-y-6">
        {/* RING */}
        <div className="relative" style={{ width: ringSize, height: ringSize }}>
          <svg
            width={ringSize}
            height={ringSize}
            className="transform -rotate-90"
          >
            {macros.map((macro, index) => {
              const baseRadius = 75;
              const spacing = 16;
              const radius = baseRadius - index * spacing;
              const circumference = 2 * Math.PI * radius;
              const offset =
                circumference - (macro.percentage / 100) * circumference;

              return (
                <motion.circle
                  key={macro.name}
                  cx={center}
                  cy={center}
                  r={radius}
                  stroke={macro.color}
                  strokeWidth="12"
                  fill="none"
                  strokeLinecap="round"
                  initial={{ strokeDashoffset: circumference }}
                  animate={{ strokeDashoffset: offset }}
                  transition={{ duration: 1.4, delay: index * 0.2 }}
                  style={{ strokeDasharray: circumference }}
                />
              );
            })}
          </svg>

          {/* CALORIES DISPLAY */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="shadow-md rounded-full p-4 px-6">
              <p className="text-2xl font-bold text-gray-900 text-center">
                {totalCalories}
              </p>
              <p className="text-sm text-gray-500 text-center -mt-1">kcal</p>
            </div>
          </div>
        </div>

        {/* LIST */}
        <div className="w-full space-y-4">
          {macros.map((macro, index) => (
            <motion.div
              key={macro.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center justify-between"
            >
              <div className="flex items-center space-x-3">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: macro.color }}
                />
                <span className="text-sm font-medium text-gray-700">
                  {macro.name}
                </span>
              </div>

              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">
                  {macro.value}g
                </p>
                <p className="text-xs text-gray-500">/ {macro.target}g</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MacroRing;
